// ============================================================
// useSharedData - Shared attacker profiles, target templates,
// and shared history sync with Firestore.
// ============================================================
// Extracted from useFirestoreSync. Handles:
//   - Shared tgt templates listener + debounced write (700ms)
//   - Shared history listener, on a screen that may undo
//   - Echo prevention via JSON string comparison
// ============================================================

import { useState, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Target, HistorySnapshot } from '../types';
import type * as mpModule from '../multiplayer';
import useMirror from './useMirror';

const PROFILE_SYNC_DEBOUNCE_MS = 700;

interface SharedHistoryCache {
  stack: HistorySnapshot[];
  pointer: number;
}

interface UseSharedDataParams {
  sessionId: string | null;
  tgtTemplates: Record<string, Target>;
  setTgtTemplates: Dispatch<SetStateAction<Record<string, Target>>>;
  /** Multiplayer service (DI) */
  mp: typeof mpModule;
  /** Surface sync failures to the user (toast); console.error stays for diagnostics */
  onSyncError?: (message: string) => void;
  /** This screen may not undo (the `undo` lock): it does not watch the history at all. */
  undoLocked?: boolean;
}

export interface UseSharedDataReturn {
  sharedHistoryState: { canUndo: boolean };
  setSharedHistoryState: Dispatch<SetStateAction<{ canUndo: boolean }>>;
  sharedHistoryCacheRef: MutableRefObject<SharedHistoryCache>;
  undoRedoBusyRef: MutableRefObject<boolean>;
}

type Templates = Record<string, Target>;

/** The template sync's refs, shared by its listener and its writer. */
interface TemplateRefs {
  /** Echo prevention: the JSON the server is believed to hold. */
  lastJsonRef: MutableRefObject<string>;
  timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  /** Nothing is written until the first view has been seen. */
  initRef: MutableRefObject<boolean>;
}

/**
 * One listener delivery: adopt a document unless it is our own echo; a
 * missing document is seeded, once, with an empty set.
 */
function applyTemplatesView(
  refs: TemplateRefs,
  seed: () => void,
  setTgtTemplates: (t: Templates) => void,
  view: { templates: Templates | null; exists: boolean },
): void {
  const { templates, exists } = view;
  if (exists && templates) {
    const json = JSON.stringify(templates);
    if (json !== refs.lastJsonRef.current) {
      refs.lastJsonRef.current = json;
      setTgtTemplates(templates);
    }
    refs.initRef.current = true;
  } else if (!exists && !refs.initRef.current) {
    refs.initRef.current = true;
    seed();
  }
}

/** Write the templates once they settle; the JSON is marked as the server's when the write goes out. */
function scheduleTemplateWrite(
  refs: TemplateRefs,
  write: () => Promise<void>,
  json: string,
  onFail: (e: unknown) => void,
) {
  if (refs.timerRef.current) clearTimeout(refs.timerRef.current);
  refs.timerRef.current = setTimeout(() => {
    refs.lastJsonRef.current = json;
    write().catch(onFail);
  }, PROFILE_SYNC_DEBOUNCE_MS);
}

/** The template listener. Seeded with an empty set when the session has none yet. */
function useTemplateListener({ sessionId, setTgtTemplates, mp }: UseSharedDataParams, refs: TemplateRefs) {
  useEffect(() => {
    if (!sessionId) return;
    const seed = () => {
      const init = {};
      refs.lastJsonRef.current = JSON.stringify(init);
      mp.setSharedTgtTemplates(sessionId, init).catch((e) => console.error('TgtTemplates init write failed:', e));
    };
    const unsub = mp.onSharedTgtTemplatesUpdate(sessionId, (templates, exists) =>
      applyTemplatesView(refs, seed, setTgtTemplates, { templates, exists }),
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
}

/**
 * The debounced write of local template changes, 700ms after they settle.
 * `onSyncError` is read through a ref that always holds the latest handler:
 * listing it in the deps would re-run this effect - and restart a pending
 * write's debounce - whenever the caller's handler changes identity, while
 * leaving it out (as it once was) reported a failure to a stale handler
 * (v8.377.6).
 */
function useTemplateWriter({ sessionId, tgtTemplates, mp, onSyncError }: UseSharedDataParams, refs: TemplateRefs) {
  const { lastJsonRef, timerRef, initRef } = refs;
  const onSyncErrorRef = useRef(onSyncError);
  onSyncErrorRef.current = onSyncError;
  useEffect(() => {
    if (!sessionId || !initRef.current) return;
    const json = JSON.stringify(tgtTemplates);
    if (json === lastJsonRef.current) return;
    scheduleTemplateWrite(
      { lastJsonRef, timerRef, initRef },
      () => mp.setSharedTgtTemplates(sessionId, tgtTemplates),
      json,
      (e) => {
        console.error('TgtTemplates sync failed:', e);
        onSyncErrorRef.current?.('Template sync failed - other players may not see your changes.');
      },
    );
  }, [tgtTemplates, sessionId, mp, lastJsonRef, timerRef, initRef]);
}

/** The shared target templates: listened to, and written 700ms after a local change settles. */
function useTgtTemplatesSync(params: UseSharedDataParams) {
  const refs: TemplateRefs = {
    lastJsonRef: useRef(''),
    timerRef: useRef<ReturnType<typeof setTimeout> | null>(null),
    initRef: useRef(false),
  };
  useTemplateListener(params, refs);
  useTemplateWriter(params, refs);
}

/**
 * One shared-history delivery. While an undo/redo is in flight it owns
 * the pointer, so only the stack is taken; otherwise the pointer and the
 * Undo button follow the server. The button's state is set only when it
 * flips (plan sync-listeners): every undoable action rewrites the history
 * twice, and a new `{ canUndo }` each time rendered the shell for nothing.
 */
function applyHistoryUpdate(
  cacheRef: MutableRefObject<SharedHistoryCache>,
  busy: boolean,
  update: { stack: HistorySnapshot[]; pointer: number; canUndo: boolean },
  shown: { canUndo: boolean; set: (s: { canUndo: boolean }) => void },
): void {
  cacheRef.current = { stack: update.stack, pointer: busy ? cacheRef.current.pointer : update.pointer };
  if (!busy && update.canUndo !== shown.canUndo) shown.set({ canUndo: update.canUndo });
}

/**
 * The shared history's listener, and the refs undo coordinates through - on a screen that may undo. One that may not
 * (the `undo` lock, closed for players by default) watches nothing (audit finding 5, 2026-11-01): its button is off
 * and Ctrl+Z refuses whatever the history says, and the stack is up to 900 kB, re-sent with every undoable action.
 * Undo is optimistic - it restores from the cached stack before the server confirms - so a screen that may press it
 * needs the whole stack, not a flag. Unlocked, it takes the history from the next delivery.
 */
function useSharedHistory(sessionId: string | null, mp: typeof mpModule, undoLocked: boolean) {
  const [sharedHistoryState, setSharedHistoryState] = useState<{ canUndo: boolean }>({ canUndo: false });
  const sharedHistoryCacheRef = useRef<SharedHistoryCache>({ stack: [], pointer: -1 });
  const undoRedoBusyRef = useRef(false);
  const shownRef = useMirror(sharedHistoryState);
  useEffect(() => {
    if (!sessionId || undoLocked) return;
    const unsub = mp.onSharedHistoryUpdate(sessionId, (update) =>
      applyHistoryUpdate(sharedHistoryCacheRef, undoRedoBusyRef.current, update, {
        canUndo: shownRef.current.canUndo,
        set: (next) => {
          shownRef.current = next;
          setSharedHistoryState(next);
        },
      }),
    );
    return () => unsub();
  }, [sessionId, mp, shownRef, undoLocked]);
  return { sharedHistoryState, setSharedHistoryState, sharedHistoryCacheRef, undoRedoBusyRef };
}

/**
 * Shared target templates and the shared history. The shared
 * attacker-profile document is GONE (v7.6.0): loadouts live on the
 * roster, and the shared doc had no concept of who owned a profile, so
 * accepting one wrote another player's grid onto whoever this browser
 * happened to be acting as.
 */
export default function useSharedData(params: UseSharedDataParams): UseSharedDataReturn {
  useTgtTemplatesSync(params);
  return useSharedHistory(params.sessionId, params.mp, !!params.undoLocked);
}
